import React, { FC, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import './JobManagementForTab.scss';
import {
    GET_ALL_JOBS_WITH_FILTERS_AND_SEARCH,
    GET_USER_REGION_DISTRICT_BY_USER_ID,
} from '../../graphql/client/queries';
import {
    GetAllJobsWithFiltersAndSearch,
    GetAllJobsWithFiltersAndSearch_getAllJobsWithFiltersAndSearch as Job,
    GetUserRegionDistrictByUserId as UserRegionDistrict,
} from '../../types/schemaTypes';
import { Loading, Error } from '../_common';
import JobManagementTable from './JobManagementTable';
import { useStoreContext } from '../App/StoreProvider';
import { Routes } from '../../utils';
import { Link } from 'react-router-dom';

// Do not change this value unless the associate backend value has been updated - (8/22/19) BS
const PAGE_SIZE = 50;

const JobManagementForTab: FC = (): React.ReactElement => {
    document.title = 'Job Management';

    const [state] = useStoreContext();
    const { auth } = state;
    const userId = auth.user ? parseInt(auth.user.profile.id) : 0;
    const [searchPhrase, setSearchPhrase] = useState('');

    const { data: userRegionDistrict, loading: userRegionDistrictLoading } = useQuery<UserRegionDistrict>(GET_USER_REGION_DISTRICT_BY_USER_ID, {
        variables: { userId: userId },
        fetchPolicy: 'no-cache',
    });

    let userRegions: number[] = [];
    let userDistricts: number[] = [];
    if (userRegionDistrict && userRegionDistrict.getUserRegionDistrictByUserId) {
        if (userRegionDistrict?.getUserRegionDistrictByUserId?.regionIds !== '')
            userRegions = userRegionDistrict?.getUserRegionDistrictByUserId?.regionIds.split(',').map(i => Number(i)) ?? [];
        if (userRegionDistrict?.getUserRegionDistrictByUserId?.districtIds !== '')
            userDistricts = userRegionDistrict?.getUserRegionDistrictByUserId?.districtIds.split(',').map(i => Number(i)) ?? [];
    }

    const { data, loading, error, fetchMore } = useQuery<GetAllJobsWithFiltersAndSearch>(GET_ALL_JOBS_WITH_FILTERS_AND_SEARCH, {
        variables: {
            input: {
                regionIds: userRegions,
                districtIds: userDistricts,
                searchString: searchPhrase,
            }
        },
        fetchPolicy: 'network-only',
        skip: userRegionDistrictLoading,
    });

    const jobsList: Job[] = [];
    if (data && data.getAllJobsWithFiltersAndSearch && data.getAllJobsWithFiltersAndSearch.length !== 0) {
        data.getAllJobsWithFiltersAndSearch.forEach((element: Job | null): void => {
            if (element !== null) {
                jobsList.push(element);
            }
        });
    }

    const offset =
        data && data.getAllJobsWithFiltersAndSearch ? data.getAllJobsWithFiltersAndSearch.length / PAGE_SIZE : 0;
    let isEndOfResults = false;
    if (
        offset % 1 !== 0 ||
        (!isEndOfResults &&
            data &&
            data.getAllJobsWithFiltersAndSearch &&
            data.getAllJobsWithFiltersAndSearch.length < PAGE_SIZE)
    ) {
        // If offset is not a whole number, the end of the results has been reached. (8/22/19) - BS
        isEndOfResults = true;
    }

    const updateSearchPhrase = (event) => {
        if (event.key === 'Enter') {
            setSearchPhrase(event.target.value)
        }
    }

    const columnsJobManagement = [
        {
            field: 'jobId',
            hide: true,
        },
        {
            field: 'jobName',
            headerName: 'WellName',
            filter: 'agSetColumnFilter',
            hide: true,
        },
        {
            field: 'checkbox',
            headerName: "",
            cellRenderer: ({ data }) => {
                return (
                    <Link
                        to={`${window.location.origin}${Routes.STAGE_SUMMARY.route}${data.jobId}`}
                        className="EquipmentProfile-ButtonText"
                    >
                        <input type="radio" style={{ cursor: "pointer" }} />
                    </Link>
                )
            },
            filter: false,
            sortable: false,
            cellStyle: { display: "flex", justifyContent: "center" },
            maxWidth: 30
        },
        {
            field: 'jobName',
            headerName: 'WellName',
            filter: 'agSetColumnFilter',
        },
        {
            field: 'region',
            headerName: 'Region',
            filter: 'agSetColumnFilter',
        },
        {
            field: 'district',
            headerName: 'District',
            filter: 'agSetColumnFilter',
        },
        {
            field: 'status',
            headerName: 'Status',
            filter: 'agSetColumnFilter',
        },
    ];

    if (error) {
        return <Error error={error} />
    }

    return (
        <div className="JobManagement-Container">
            <div className="Header-Container">
                <div className="Header-SearchBar">
                    <div className="SearchBar-Container">
                        <i className="material-icons SearchBar-Icon">search</i>
                        <input
                            id="textSearch"
                            type="text"
                            placeholder="Search"
                            className="SearchBar-Input"
                            onKeyDown={event => updateSearchPhrase(event)}
                        />
                    </div>
                </div>
                <div className="Header-ListHeader">
                    <div className="Header-LeftOptions"></div></div>
            </div>

            <div>
                {(loading || userRegionDistrictLoading) ? (
                    <div className="JobManagementForTab-Spinner">
                        <Loading />
                    </div>
                ) : jobsList.length > 0 ? (
                    <JobManagementTable
                        jobsList={jobsList}
                        columnsJobManagement={columnsJobManagement}
                        isLoading={loading}
                        isEndOfResults={isEndOfResults}
                        onLoadMore={() =>
                            fetchMore({
                                variables: {
                                    offset,
                                },
                                updateQuery: (prev, { fetchMoreResult }) => {
                                    if (
                                        prev &&
                                        fetchMoreResult &&
                                        prev.getAllJobsWithFiltersAndSearch &&
                                        fetchMoreResult.getAllJobsWithFiltersAndSearch
                                    ) {
                                        return Object.assign({}, prev, {
                                            getAllJobsWithFiltersAndSearch: [
                                                ...prev.getAllJobsWithFiltersAndSearch,
                                                ...fetchMoreResult.getAllJobsWithFiltersAndSearch,
                                            ],
                                        });
                                    } else {
                                        return prev;
                                    }
                                },
                            })
                        }
                    />
                ) : (
                    <div className="JobManagementForTab-NoResult-Container">
                        <i className="material-icons NoResult-Icon">filter_list</i>
                        <div className="NoResult-First-Title-Primary">No matching items found</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobManagementForTab;

